/**
 * Complete The Look Collection Carousel Web Component
 * Manages a Swiper.js carousel with product variants, quick add functionality,
 * and synchronization with main product selections.
 */
class CompleteTheLookCollectionCarousel extends HTMLElement {
  // Class constants
  static SELECTORS = {
    CAROUSEL: (sectionId) => `#collection-carousel-${sectionId}`,
    VARIANT_DATA: '[id^="tyr-variants-"]',
    PRODUCT_DATA: '#tyr-variants-product-info',
    QUICK_ADD_PLUS: '.tyr-quick-add__plus',
    QUICK_ADD_MESSAGE: '.tyr-quick-add__added-message',
    QUICK_ADD_ERROR_MESSAGE_WRAPPER: '.tyr-quick-add__error-message-wrapper',
    QUICK_ADD_ERROR_MESSAGE: '.tyr-quick-add__error-message',
    SIZE_SELECT: '.tyr-quick-add__size-select',
    SWATCH_INPUT: '.swatch-input__input',
    SWATCH_CHECKED: '.swatch-input__input:checked',
    PRODUCT_IMAGE: '.product-card__main-image',
    PRODUCT_LINKS: 'a[href*="/products/"]',
    CART_DRAWER: 'cart-drawer',
    CART_DRAWER_ITEMS: 'cart-drawer-items',
    MAIN_PRODUCT_VARIANT: [
      '.product-form__input .form__label [data-selected-value]',
      '.product-form__input input[type="radio"]:checked',
      '.product-form__input .swatch-input__input:checked'
    ],
    NAVIGATION: {
      CONTAINER: '.collection-carousel-section, .complete-the-look-section',
      NEXT: '.swiper-button-next',
      PREV: '.swiper-button-prev',
      FALLBACK_NEXT: '.complete-the-look-section .swiper-button-next, .tyr-collection-carousel__content_navigation .swiper-button-next',
      FALLBACK_PREV: '.complete-the-look-section .swiper-button-prev, .tyr-collection-carousel__content_navigation .swiper-button-prev'
    }
  };

  static DEFAULTS = {
    AUTOPLAY_DELAY: 2500,
    SYNC_DELAY: 100,
    ANIMATION_DURATION: 500,
    MESSAGE_TIMEOUT: 1200,
    MESSAGE_TIMEOUT_LONG: 3000,
    SCROLL_TIMEOUT: 150,
    BREAKPOINTS: {
      0: { slidesPerView: 'auto' },
      1024: { slidesPerView: 2.5 },
    }
  };

  constructor() {
    super();
    this.swiper = null;
    this.unsubscribeCallbacks = [];
    this.scrollTimeout = null;

    // Bind methods to preserve context
    this.handleQuickAdd = this.handleQuickAdd.bind(this);
    this.handleSizeChange = this.handleSizeChange.bind(this);
    this.handleSwatchChange = this.handleSwatchChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.syncWithMainProduct = this.syncWithMainProduct.bind(this);
  }

  connectedCallback() {
    try {
      this.validateDependencies();
      this.initializeCarousel();
      this.setupEventListeners();
      this.subscribeToMainProductChanges();

      // Additional sync after full initialization to catch pre-selected variants
      setTimeout(() => {
        this.syncWithMainProduct();
      }, 300);
    } catch (error) {
      console.error('Failed to initialize CompleteTheLookCollectionCarousel:', error);
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Validates required dependencies and DOM elements
   */
  validateDependencies() {
    if (typeof Swiper === 'undefined') {
      throw new Error('Swiper.js is required but not loaded');
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      throw new Error('Section ID is required');
    }

    const swiperElement = this.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.CAROUSEL(sectionId));
    if (!swiperElement) {
      throw new Error(`Carousel element not found for section: ${sectionId}`);
    }
  }

  /**
   * Initializes the Swiper carousel with configuration
   */
  initializeCarousel() {
    const sectionId = this.dataset.sectionId;
    const swiperElement = this.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.CAROUSEL(sectionId));

    const config = this.buildSwiperConfig();
    this.swiper = new Swiper(swiperElement, config);

    this.setupScrollControl();
  }

  /**
   * Builds Swiper configuration from dataset attributes
   */
  buildSwiperConfig() {
    const {
      allowMousewheel = 'false',
      allowAutoplay = 'false',
      playInLoop = 'true',
      preventClicks = 'false',
      autoplayDelay = CompleteTheLookCollectionCarousel.DEFAULTS.AUTOPLAY_DELAY.toString(),
      autoplayDisableOnInteraction = 'true'
    } = this.dataset;

    const config = {
      mousewheel: allowMousewheel === 'true',
      spaceBetween: 0,
      loop: playInLoop === 'true',
      navigation: this.getNavigationElements(),
      breakpoints: CompleteTheLookCollectionCarousel.DEFAULTS.BREAKPOINTS,
      on: {
        init: () => {
          // Delay initial sync to ensure DOM is fully ready
          setTimeout(() => {
            this.syncWithMainProduct();
          }, 200);
        }
      }
    };

    if (allowAutoplay === 'true') {
      config.autoplay = {
        delay: parseInt(autoplayDelay, 10),
        disableOnInteraction: autoplayDisableOnInteraction === 'true'
      };
    }

    return config;
  }

  /**
   * Gets navigation elements for Swiper
   */
  getNavigationElements() {
    const container = this.closest(CompleteTheLookCollectionCarousel.SELECTORS.NAVIGATION.CONTAINER);

    let nextButton = container?.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.NAVIGATION.NEXT);
    let prevButton = container?.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.NAVIGATION.PREV);

    // Fallback to document-level selectors
    if (!nextButton || !prevButton) {
      nextButton = document.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.NAVIGATION.FALLBACK_NEXT);
      prevButton = document.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.NAVIGATION.FALLBACK_PREV);
    }

    return { nextEl: nextButton, prevEl: prevButton };
  }

  /**
   * Sets up scroll control for mousewheel
   */
  setupScrollControl() {
    if (this.dataset.allowMousewheel === 'true') {
      window.addEventListener('scroll', this.handleScroll);
    }
  }

  /**
   * Handles scroll events to control mousewheel
   */
  handleScroll() {
    if (this.swiper?.mousewheel?.enabled) {
      this.swiper.mousewheel.disable();
    }

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if (this.swiper?.mousewheel) {
        this.swiper.mousewheel.enable();
      }
    }, CompleteTheLookCollectionCarousel.DEFAULTS.SCROLL_TIMEOUT);
  }

  /**
   * Sets up all event listeners
   */
  setupEventListeners() {
    this.addEventListener('click', this.handleQuickAdd);
    this.addEventListener('change', this.handleSizeChange);
    this.addEventListener('change', this.handleSwatchChange, true);
  }

  /**
   * Handles quick add button clicks
   */
  async handleQuickAdd(event) {
    const plusButton = event.target.closest(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);
    if (!plusButton) return;

    event.stopPropagation();
    event.preventDefault();

    const card = plusButton.closest('.swiper-slide');
    const variantId = plusButton.dataset.variantId;

    if (!card || !variantId) {
      console.warn('Invalid quick add configuration');
      return;
    }

    await this.addToCart(plusButton, card, variantId);
  }

  /**
   * Adds product to cart
   */
  async addToCart(button, card, variantId) {
    this.setButtonLoading(button, true);

    try {
      const formData = this.buildCartFormData(variantId);
      const response = await this.submitToCart(formData);

      if (response.status) {
        throw new Error(response.message || 'Failed to add to cart');
      }

      this.showSuccessMessage(button, card);
      this.updateCartDrawer(response);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage(button, error, card);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  /**
   * Builds form data for cart submission
   */
  buildCartFormData(variantId) {
    const formData = new FormData();
    formData.append('items[0][id]', variantId);
    formData.append('items[0][quantity]', 1);

    const cartDrawer = document.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.CART_DRAWER);
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sections = cartDrawer.getSectionsToRender();
      formData.append('sections', sections.map(s => s.id));
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement?.(document.activeElement);
    }

    return formData;
  }

  /**
   * Submits form data to cart
   */
  async submitToCart(formData) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests.')
      }

      const data = await response.json();
      throw new Error(data?.message || data?.description || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Sets button loading state
   */
  setButtonLoading(button, isLoading) {
    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
  }

  /**
   * Shows success message temporarily
   */
  showSuccessMessage(button, card) {
    const message = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_MESSAGE);
    if (!message) return;

    button.style.display = 'none';
    message.style.display = 'inline';

    setTimeout(() => {
      message.style.display = 'none';
      button.style.display = 'inline-block';
    }, CompleteTheLookCollectionCarousel.DEFAULTS.MESSAGE_TIMEOUT);
  }

  /**
   * Shows error message temporarily
   */
  showErrorMessage(button, message, card) {
    const messageWrapperElement = card.querySelector(
      CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_ERROR_MESSAGE_WRAPPER
    );
    const messageElement = card.querySelector(
      CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_ERROR_MESSAGE
    );

    if (!messageWrapperElement || !messageElement) return;

    button.style.disabled = true;

    messageElement.innerText = message;
    messageWrapperElement.toggleAttribute('hidden', false);

    setTimeout(() => {
      messageWrapperElement.toggleAttribute('hidden', true);
      button.style.disabled = false;
    }, CompleteTheLookCollectionCarousel.DEFAULTS.MESSAGE_TIMEOUT_LONG);
  }

  /**
   * Updates cart drawer with response
   */
  updateCartDrawer(response) {
    const cartDrawer = document.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.CART_DRAWER);

    if (cartDrawer) {
      if (response.items?.length > 0) {
        cartDrawer.classList.remove('is-empty');
        const drawerItems = cartDrawer.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.CART_DRAWER_ITEMS);
        drawerItems?.classList.remove('is-empty');
      }

      if (typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(response);
      }
    } else {
      window.location = window.routes?.cart_url || '/cart';
    }
  }

  /**
   * Handles size dropdown changes
   */
  handleSizeChange(event) {
    const sizeSelect = event.target.closest(CompleteTheLookCollectionCarousel.SELECTORS.SIZE_SELECT);
    if (!sizeSelect) return;

    const card = sizeSelect.closest('.swiper-slide');
    if (!card) return;

    this.updateQuickAddForSizeChange(card, sizeSelect.value);
  }

  /**
   * Updates quick add button when size changes
   */
  updateQuickAddForSizeChange(card, selectedSize) {
    const productData = this.getProductData(card);
    if (!productData) return;

    const selectedColor = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_CHECKED)?.value;
    if (!selectedColor || !selectedSize) return;

    const matchedVariant = this.findMatchingVariant(productData.variants, selectedColor, selectedSize);
    const plusButton = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);

    if (matchedVariant && plusButton) {
      plusButton.dataset.variantId = matchedVariant.id;
      plusButton.disabled = false;
    } else if (plusButton) {
      plusButton.disabled = true;
    }
  }

  /**
   * Handles swatch color changes
   */
  handleSwatchChange(event) {
    if (!event.target.matches(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_INPUT)) return;

    event.stopPropagation();
    event.preventDefault();

    const swatch = event.target;
    const card = swatch.closest('.swiper-slide');

    if (card) {
      this.updateCardFromSwatch(card, swatch);
    }
  }

  /**
   * Subscribes to main product variant changes
   */
  subscribeToMainProductChanges() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return;
    }

    const events = [
      PUB_SUB_EVENTS.optionValueSelectionChange,
      PUB_SUB_EVENTS.variantChange
    ];

    events.forEach(eventType => {
      if (eventType) {
        const unsubscribe = subscribe(eventType, () => {
          setTimeout(this.syncWithMainProduct, CompleteTheLookCollectionCarousel.DEFAULTS.SYNC_DELAY);
        });
        this.unsubscribeCallbacks.push(unsubscribe);
      }
    });
  }

  /**
   * Syncs carousel with main product variant
   */
  syncWithMainProduct() {
    const currentVariant = this.getCurrentMainProductVariant();
    if (!currentVariant) return;

    const productCards = Array.from(this.querySelectorAll('.swiper-slide'));
    const matchingCards = [];
    const nonMatchingCards = [];

    // Separate matching and non-matching cards
    productCards.forEach(card => {
      const hasMatch = this.syncCardWithVariant(card, currentVariant);
      if (hasMatch) {
        matchingCards.push(card);
      } else {
        nonMatchingCards.push(card);
      }
    });

    if (matchingCards.length > 0 && this.swiper) {
      // Reorder the slides: matching cards first, then non-matching
      this.reorderSlides([...matchingCards, ...nonMatchingCards]);
    }
  }

  /**
   * Reorders slides in the carousel by moving DOM elements
   */
  reorderSlides(orderedCards) {
    if (!this.swiper || !orderedCards.length) return;

    const swiperWrapper = this.swiper.wrapperEl;
    if (!swiperWrapper) return;

    // Temporarily disable Swiper to prevent conflicts
    this.swiper.disable();

    // Remove all slides from DOM (but keep references)
    orderedCards.forEach(card => {
      if (card.parentNode === swiperWrapper) {
        swiperWrapper.removeChild(card);
      }
    });

    // Re-append slides in the new order
    orderedCards.forEach(card => {
      swiperWrapper.appendChild(card);
    });

    // Re-enable Swiper and update
    this.swiper.enable();
    this.swiper.update();

    // Go to first slide to show the matching products
    setTimeout(() => {
      this.swiper.slideTo(0, CompleteTheLookCollectionCarousel.DEFAULTS.ANIMATION_DURATION);
    }, 50);
  }

  /**
   * Gets current main product variant
   */
  getCurrentMainProductVariant() {
    for (const selector of CompleteTheLookCollectionCarousel.SELECTORS.MAIN_PRODUCT_VARIANT) {
      const element = document.querySelector(selector);
      if (!element) continue;

      let value = null;
      if (element.hasAttribute('data-selected-value')) {
        value = element.textContent?.trim();
      } else if (element.type === 'radio' && element.checked) {
        value = element.value?.trim();
      }

      if (value) {
        const extractedColor = this.extractColorFromVariant(value);
        return extractedColor;
      }
    }

    // Fallback: try to get from URL parameters (for page refresh scenarios)
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      const variantInfo = this.getVariantInfoFromId(variantId);
      if (variantInfo) {
        return this.extractColorFromVariant(variantInfo);
      }
    }

    return null;
  }

  /**
   * Gets variant information from variant ID (fallback for URL-based selections)
   */
  getVariantInfoFromId(variantId) {
    // Try to find variant info from any available product data on the page
    const productJsonElements = document.querySelectorAll('[type="application/json"]');

    for (const element of productJsonElements) {
      try {
        const data = JSON.parse(element.textContent);
        if (data.variants) {
          const variant = data.variants.find(v => v.id.toString() === variantId);
          if (variant && variant.options && variant.options.length > 0) {
            // Return the first option which is typically the color
            return variant.options[0];
          }
        }
      } catch (error) {
        // Continue to next element if JSON parsing fails
        continue;
      }
    }

    return null;
  }

  /**
   * Extracts color from variant string (removes leading numbers)
   */
  extractColorFromVariant(variantString) {
    if (!variantString) return variantString;
    return variantString.replace(/^\d+\s*/, '').trim() || variantString;
  }



  /**
   * Syncs card with variant value
   */
  syncCardWithVariant(card, variantValue) {
    const swatches = card.querySelectorAll(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_INPUT);
    let matchingSwatch = null;

    // First, try to find a matching swatch in the DOM
    swatches.forEach(swatch => {
      const swatchColor = this.extractColorFromVariant(swatch.value?.trim());
      const isMatch = swatchColor === variantValue ||
                     swatchColor?.toLowerCase() === variantValue?.toLowerCase();

      swatch.checked = isMatch;
      if (isMatch) {
        matchingSwatch = swatch;
      }
    });

    if (matchingSwatch) {
      this.updateCardFromSwatch(card, matchingSwatch);
      matchingSwatch.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    // Fallback: If no visible swatch matches, try to find the variant in product data
    // This handles cases where the matching color is beyond the swatches_count limit
    const productData = this.getProductData(card);
    if (productData && productData.variants) {
      const matchingVariant = this.findVariantByColor(productData.variants, variantValue);
      if (matchingVariant) {
        this.updateCardFromVariantData(card, matchingVariant, variantValue);
        return true;
      }
    }

    return false;
  }

  /**
   * Updates card based on selected swatch
   */
  updateCardFromSwatch(card, swatch) {
    this.updateProductImage(card, swatch);
    this.updateProductLinks(card, swatch);
    this.updateOtherSwatches(card, swatch);
    this.updateQuickAddDropdown(card);
    this.updateQuickAddBtn(card, swatch);
  }

  updateQuickAddBtn(card, swatch) {
    const plusButton = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);
    if (plusButton) {
      plusButton.dataset.variantId = swatch.dataset.variantId;
      plusButton.disabled = false;
    }
  }

  /**
   * Updates product image based on swatch selection
   */
  updateProductImage(card, swatch) {
    const productImage = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.PRODUCT_IMAGE);
    if (!productImage) return;

    const variantImage = swatch.dataset.variantImage;
    const defaultImage = productImage.dataset.defaultImage;

    if (variantImage) {
      productImage.src = variantImage;
      productImage.removeAttribute('srcset');
      productImage.removeAttribute('sizes');
    } else if (defaultImage) {
      productImage.src = defaultImage;
    }
  }

  /**
   * Updates product links with variant ID
   */
  updateProductLinks(card, swatch) {
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    const productLinks = card.querySelectorAll(CompleteTheLookCollectionCarousel.SELECTORS.PRODUCT_LINKS);
    productLinks.forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }

  /**
   * Updates other swatches in the same fieldset
   */
  updateOtherSwatches(card, selectedSwatch) {
    const swatchFieldset = selectedSwatch.closest('.product-form__input');
    if (!swatchFieldset) return;

    const otherSwatches = swatchFieldset.querySelectorAll(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_INPUT);
    otherSwatches.forEach(swatch => {
      if (swatch !== selectedSwatch) {
        swatch.checked = false;
      }
    });
  }

  /**
   * Updates quick add dropdown based on selected color
   */
  updateQuickAddDropdown(card) {
    const productData = this.getProductData(card);
    if (!productData) return;

    const sizeSelect = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.SIZE_SELECT);
    const plusButton = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);
    const selectedColor = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_CHECKED)?.value;

    if (!sizeSelect || !selectedColor) return;

    this.updateSizeOptions(sizeSelect, productData.variants, selectedColor);
    this.selectFirstAvailableSize(sizeSelect, productData.variants, selectedColor, plusButton);
  }

  /**
   * Gets product data from card
   */
  getProductData(card) {
    const productDataElement = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.PRODUCT_DATA);
    if (!productDataElement) return null;

    try {
      return JSON.parse(productDataElement.textContent);
    } catch (error) {
      console.error('Failed to parse product data:', error);
      return null;
    }
  }

  /**
   * Updates size option availability
   */
  updateSizeOptions(sizeSelect, variants, selectedColor) {
    sizeSelect.querySelectorAll('option').forEach(option => {
      const match = this.findMatchingVariant(variants, selectedColor, option.value);
      option.disabled = !match;
    });
  }

  /**
   * Selects first available size and updates quick add button
   */
  selectFirstAvailableSize(sizeSelect, variants, selectedColor, plusButton) {
    const firstAvailable = [...sizeSelect.options].find(opt => !opt.disabled);

    if (firstAvailable) {
      sizeSelect.value = firstAvailable.value;
      const matchedVariant = this.findMatchingVariant(variants, selectedColor, firstAvailable.value);

      if (matchedVariant && plusButton) {
        plusButton.dataset.variantId = matchedVariant.id;
        plusButton.disabled = false;
        sizeSelect.disabled = false;
      }
    } else {
      sizeSelect.disabled = true;
      if (plusButton) plusButton.disabled = true;
    }
  }

  /**
   * Finds matching variant by color and size
   */
  findMatchingVariant(variants, color, size) {
    return variants.find(variant =>
      variant.available &&
      variant.options.includes(color) &&
      variant.options.includes(size)
    );
  }

  /**
   * Finds a variant by its color option value
   */
  findVariantByColor(variants, colorOptionValue) {
    return variants.find(variant => {
      if (!variant.available) return false;

      // Check if any of the variant's options match the color value
      return variant.options.some(option => {
        const extractedColor = this.extractColorFromVariant(option?.trim());
        return extractedColor === colorOptionValue ||
               extractedColor?.toLowerCase() === colorOptionValue?.toLowerCase();
      });
    });
  }

  /**
   * Updates card based on variant data (fallback when no DOM swatch exists)
   */
  updateCardFromVariantData(card, variant, colorOptionValue) {
    // Clear any existing swatch selections
    const allSwatches = card.querySelectorAll(CompleteTheLookCollectionCarousel.SELECTORS.SWATCH_INPUT);
    allSwatches.forEach(swatch => swatch.checked = false);

    // Update product links with the variant ID
    const productLinks = card.querySelectorAll(CompleteTheLookCollectionCarousel.SELECTORS.PRODUCT_LINKS);
    productLinks.forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });

    // Update product image if available from variant data
    const productImage = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.PRODUCT_IMAGE);
    if (productImage && variant.variantData && variant.variantData.featured_image) {
      productImage.src = variant.variantData.featured_image.src;
      productImage.removeAttribute('srcset');
      productImage.removeAttribute('sizes');
    }

    // Update quick add functionality
    const plusButton = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);
    if (plusButton) {
      plusButton.dataset.variantId = variant.id;
      plusButton.disabled = false;
    }

    // Update size dropdown to reflect available sizes for this color
    this.updateQuickAddDropdownFromVariantData(card, colorOptionValue);
  }

  /**
   * Updates quick add dropdown based on variant data (fallback method)
   */
  updateQuickAddDropdownFromVariantData(card, selectedColor) {
    const productData = this.getProductData(card);
    if (!productData) return;

    const sizeSelect = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.SIZE_SELECT);
    const plusButton = card.querySelector(CompleteTheLookCollectionCarousel.SELECTORS.QUICK_ADD_PLUS);

    if (!sizeSelect || !selectedColor) return;

    // Update size options based on available variants for this color
    this.updateSizeOptions(sizeSelect, productData.variants, selectedColor);
    this.selectFirstAvailableSize(sizeSelect, productData.variants, selectedColor, plusButton);
  }

  /**
   * Cleanup method for component destruction
   */
  cleanup() {
    // Unsubscribe from events
    this.unsubscribeCallbacks.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    this.unsubscribeCallbacks = [];

    // Clear timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }

    // Remove scroll listener
    window.removeEventListener('scroll', this.handleScroll);

    // Destroy Swiper
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Register the custom element
customElements.define('complete-the-look-carousel', CompleteTheLookCollectionCarousel);
